
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import ListBase from '@/lib/listbase';
import FormBase from '@/components/helper/form-base.vue';
import SelectedDispache from './selected_dispaches/selected_dispache.vue';
import DateRangePicker from '../components/date-range-picker.vue';
import { getObjectivesSummary, getTransactionSummary, storeOffices } from './selected_dispaches/services/transaction-service';
import { applyObjectivesToSummary } from './selected_dispaches/utils/internal-utils';
import { shouldIncludeCenter } from './selected_dispaches/utils/table-builder';
import { checkPermissions } from '../utils/check-permissions';
import ActionButtons from '../components/action-buttons.vue';
import ImportChanges from './selected_dispaches/import-changes.vue';
import SaveConfirmationDialog from '../components/save-confirmation-dialog.vue';

interface SelectedDispacheRef extends Vue {
    showConfirmation: () => void;
    cancelImportedData: () => void;
    updateTableWithImportedData: (sheet: any) => void;
    saveData: () => Promise<void>;
}

const VIEW_MAP = [
    { name: 'internal', label: 'Despachos AASA - Internos', index: 0 },
    { name: 'external', label: 'Despachos AASA - Externos', index: 1 },
    { name: 'total', label: 'Despachos AASA - Total', index: 2 },
];

@Component({
    components: {
        FormBase,
        SelectedDispache,
        DateRangePicker,
        ActionButtons,
        ImportChanges,
        SaveConfirmationDialog
    }
})

export default class Dispatches extends ListBase {
    @Prop({ type: String, default: '' }) readonly marcaSeleccionada!: string;

    tabModel: any = null;
    selectedOptions: string[] = [];
    filterOptions: string[] = [];
    allowMultipleSelection: boolean = false;

    simaLoading: boolean = false;
    dataLoading: boolean = false;

    showImport: boolean = false;
    isEdited: boolean = false;
    confirmSave: boolean = false;
    @Ref('dispacheInternal') readonly dispacheInternal!: SelectedDispacheRef;
    @Ref('dispacheExternal') readonly dispacheExternal!: SelectedDispacheRef;
    @Ref('dispacheTotal') readonly dispacheTotal!: SelectedDispacheRef;
    initialLoad = true;
    importType: number = 0;
    lastImportedType: 'internal' | 'external' | 'total' | null = null;
    importedSheets: Record<string, { headers: string[]; items: any[] } | null> = {
        internal: null, external: null, total: null
    };

    justImported: boolean = false;
    justEdited: boolean = false;

    summaryData: any = null;

    currentYear: number = new Date().getFullYear();

    rangeFrom: any = {
        from: 0,
        to: new Date().getMonth(),
        fromText: 'Enero',
        toText: this.gCommonList('months')[new Date().getMonth()].name,
        year: this.currentYear
    };

    @Watch('importType', { immediate: true })
    onImportTypeChanged(newVal: string | number, oldVal: string | number) {
        const entry = VIEW_MAP.find(item => item.name === newVal);
        this.lastImportedType = entry ? entry.name as 'internal' | 'external' | 'total' : null;
        this.tabModel = this.lastImportedType;
        console.log(`[Dispatches] 📋 importType cambió de`, oldVal, `a`, newVal);
    }

    get isDealer() {
        return false;
    }

    get loading() {
        return this.dataLoading;
    }

    get brandId() {
        return this.marcaSeleccionada.split('|')[0];
    }

    get brandName() {
        return this.marcaSeleccionada.split('|')[1];
    }

    get userRol() {
        return this.$store.state.session &&
            this.$store.state.session.user &&
            this.$store.state.session.user.roles
            ? this.$store.state.session.user.roles.map((role: any) => role.id)
            : [];
    }

    get userPermissions() {
        const roleIds = this.userRol;
        return checkPermissions(roleIds);
    }

    get hasView() {
        return this.userPermissions.view;
    }

    get hasAdmin() {
        return this.userPermissions.admin;
    }

    get monthHeaders(): string[] {
        const months = this.gCommonList('months').map(m => m.name);
        return months.slice(this.rangeFrom.from, this.rangeFrom.to + 1);
    }

    get tabs() {
        return VIEW_MAP.filter(tab => (this.hasData as any)[tab.name]);
    }

    get activeDispacheRef(): SelectedDispacheRef | null {
        switch (this.tabModel) {
            case 'internal': return this.dispacheInternal;
            case 'external': return this.dispacheExternal;
            case 'total': return this.dispacheTotal;
            default: return null;
        }
    }

    async onConfirmSave() {
        console.log(
            `[Dispatches] 💾 onConfirmSave — justImported:`, this.justImported,
            ` justEdited:`, this.justEdited,
            ` lastImportedType:`, this.lastImportedType,
            ` tabModel:`, this.tabModel,
        );

        this.confirmSave = false;

        let ref: SelectedDispacheRef | null = null;

        if (this.justImported && this.lastImportedType) {
            ref = {
                internal: this.dispacheInternal,
                external: this.dispacheExternal,
                total: this.dispacheTotal
            }[this.lastImportedType];
            this.justImported = false;
        } else if (this.justEdited) {
            ref = this.activeDispacheRef;
            this.justEdited = false;
        } else {
            ref = this.activeDispacheRef;
        }

        if (!ref) {
            console.error('[Dispatches] ❌ No se encontró ref activa; justImported=', this.justImported);
            return;
        }

        if (typeof ref.saveData !== 'function') {
            console.error('[Dispatches] ❌ La ref no expone saveData():', ref);
            return;
        }

        console.log('👉 Voy a llamar a saveData() de:', ref);

        try {
            console.log('[Dispatches] ▶️ Llamando a child.saveData() …');
            await ref.saveData();
            console.log('[Dispatches] ✅ child.saveData() completado sin errores');
            this.isEdited = false;
            this.justEdited = false;
            this.justImported = false;
            this.getTransactionSummary();
        } catch (e) {
            console.error('[Dispatches] 💥 Error en saveData desde padre:', e);
        }
    }

    handleImportSuccess() {
        this.showImport = false;
        this.justImported = true;
    }

    handleDataSaved() {
        this.isEdited = false;
        this.getTransactionSummary();
    }

    onChildEdit() {
        this.isEdited = true;
        this.justEdited = true;
        this.justImported = false;
        const entry = VIEW_MAP.find(tab => tab.index === this.importType);
        this.lastImportedType = entry
            ? (entry.name as 'internal' | 'external' | 'total')
            : null;
        this.tabModel = this.lastImportedType;
        console.log('[Dispatches] onchildEdit → lastImportedType:', this.lastImportedType);
    }

    onSaveConfirm() {
        if (this.activeDispacheRef) {
            this.activeDispacheRef.showConfirmation();
        }
    }

    onCancelImport() {
        this.importedSheets = { internal: null, external: null, total: null };
        ['Internal', 'External', 'Total'].forEach(suffix => {
            const ref = (this.$refs as any)[`dispache${suffix}`];
            if (ref?.cancelImportedData) {
                ref.cancelImportedData();
            }
        });
        this.isEdited = false;
    }

    onUpdateObjectives({ type, sheet }: { type: 'internal' | 'external' | 'total'; sheet: { headers: string[]; items: any[] } }) {
        this.importedSheets[type] = sheet;
        const refMap = {
            internal: this.dispacheInternal,
            external: this.dispacheExternal,
            total: this.dispacheTotal
        };
        refMap[type]?.updateTableWithImportedData(sheet);
        this.justImported = true;
        this.lastImportedType = type;
        this.isEdited = true;
    }

    public updateRangeFrom(newRange: any): void {
        this.dataLoading = true;
        this.rangeFrom.from = newRange.rangeFrom;
        this.rangeFrom.to = newRange.rangeTo;
        this.rangeFrom.fromText = newRange.rangeFromMonth;
        this.rangeFrom.toText = newRange.rangeToMonth;
        this.rangeFrom.year = newRange.year;
        this.getTransactionSummary();
    }

    public updateRangeYear(newYear: number): void {
        this.dataLoading = true;
        if (newYear <= this.currentYear) {
            this.rangeFrom.year = newYear;
        } else {
            this.rangeFrom.year = this.currentYear;
        }
        this.getTransactionSummary();
    }

    public updateMonth(newMonthIndex: number): void {
        this.dataLoading = true;
        this.rangeFrom.from = newMonthIndex;
        this.rangeFrom.to = newMonthIndex;
        const months = this.gCommonList('months');
        this.rangeFrom.fromText = months[newMonthIndex].name;
        this.rangeFrom.toText = months[newMonthIndex].name;
        this.getTransactionSummary();
    }

    mounted() {
        this.getTransactionSummary();
    }

    selectOption(option: string) {
        this.selectedOptions = [option];
        this.$emit('update-brand', option);
    }

    async storeOfficesData() {
        this.simaLoading = true;
        try {
            const result = await storeOffices(this.$store);
            console.log('Resultado de storeOffices:', result);

            if (result && typeof result === 'object') {
                if (result.success) {
                    this.$toast.success(result.message || 'Datos guardados correctamente');
                } else {
                    this.$toast.error(result.error || 'Error al guardar datos');
                }
            } else {
                console.warn('Respuesta inesperada del servidor:', result);
                this.$toast.error('Respuesta inesperada al guardar datos');
            }
        } catch (error) {
            console.error('Error en storeOfficesData:', error);
            const errorMsg = (error as any)?.response?.data?.error || 'Error desconocido al guardar datos';
            this.$toast.error(errorMsg);
        } finally {
            this.simaLoading = false;
            this.getTransactionSummary();
        }
    }

    handleDataLoading() {
        this.dataLoading = true;
    }

    handleDataLoaded() {
        this.dataLoading = false;
    }

    handleGoBack() {
        this.$emit('go-back');
    }

    async getTransactionSummary() {
        this.dataLoading = true;
        try {
            const selectedYear = this.rangeFrom.year;
            const mesInicio = this.rangeFrom.from + 1;
            const mesFin = this.rangeFrom.to + 1;
            const payload = {
                marca: this.brandName,
                anio: selectedYear,
                mes_inicio: mesInicio,
                mes_fin: mesFin
            };
            const result = await getTransactionSummary(this.$store, payload);
            console.log('Resumen de despachos:', result);

            const objResult = await getObjectivesSummary(this.$store, payload);
            console.log('Resumen de objetivos:', objResult);

            applyObjectivesToSummary(objResult, result);

            this.summaryData = result;
            if (this.initialLoad) {
                this.resetTabModel()
                this.initialLoad = false
            }
        } catch (error) {
            const err = error as any;
            if (err.response && err.response.data) {
                console.error('Error en el backend:', err.response.data);
            } else {
                console.error('Error desconocido:', err);
            }
        } finally {
            this.dataLoading = false;
        }
    }

    private resetTabModel() {
        if (this.hasData.internal) {
            this.tabModel = 'internal';
        } else if (this.hasData.external) {
            this.tabModel = 'external';
        } else if (this.hasData.total) {
            this.tabModel = 'total';
        } else {
            this.tabModel = null;
        }
    }

    public checkDataForViewType(viewType: string): boolean {
        if (!this.summaryData) return false;

        const hasDataInCenters = (centers: { [key: string]: any }) => {
            return Object.entries(centers).some(([center, data]) => {
                if (!shouldIncludeCenter(center, viewType)) return false;

                const detalle = Array.isArray(data)
                    ? data
                    : data?.Detalle || [];
                return detalle.length > 0;
            });
        };

        return hasDataInCenters(this.summaryData.actual)
            || hasDataInCenters(this.summaryData.anio_anterior);
    }

    get hasData() {
        const hasInternal = this.checkDataForViewType('internal');
        const hasExternal = this.checkDataForViewType('external');
        const hasTotal = this.checkDataForViewType('total');
        return {
            internal: hasInternal,
            external: hasExternal,
            total: hasTotal,
        };
    }
}
